/**
 * Validation utility functions for form validation
 * Used across all forms in the application
 */

import { Employee } from '../types/employee';
import { Project } from '../types/project';

// ============================================================================
// FIELD-LEVEL VALIDATORS
// ============================================================================

/**
 * Validate required field
 */
export const validateRequired = (value: any, fieldName: string): string | null => {
  if (value === null || value === undefined || value === '') {
    return `${fieldName} is required`;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return `${fieldName} is required`;
  }
  return null;
};

/**
 * Validate email format
 * RFC 5322 compliant email regex
 */
export const validateEmail = (email: string): string | null => {
  if (!email) return null; // Allow empty for optional fields

  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(email)) {
    return 'Invalid email format';
  }
  return null;
};

/**
 * Validate phone number format
 * Accepts various formats: +1-234-567-8900, (123) 456-7890, 123-456-7890, etc.
 */
export const validatePhone = (phone: string): string | null => {
  if (!phone) return null; // Allow empty for optional fields

  // Remove all non-digit characters for length check
  const digitsOnly = phone.replace(/\D/g, '');

  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    return 'Phone number must be between 10 and 15 digits';
  }

  // Check if it contains only valid characters (digits, spaces, +, -, (, ))
  const phoneRegex = /^[\d\s+()-]+$/;
  if (!phoneRegex.test(phone)) {
    return 'Phone number contains invalid characters';
  }

  return null;
};

/**
 * Validate date range
 * Ensures end date is after start date
 */
export const validateDateRange = (
  startDate: string,
  endDate: string,
  fieldNames?: { start: string; end: string }
): string | null => {
  if (!startDate || !endDate) return null; // Skip if either is empty

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 'Invalid date format';
  }

  if (end <= start) {
    const startLabel = fieldNames?.start || 'End date';
    const endLabel = fieldNames?.end || 'Start date';
    return `${startLabel} must be after ${endLabel}`;
  }

  return null;
};

/**
 * Validate percentage value
 */
export const validatePercentage = (
  value: number,
  min: number = 0,
  max: number = 100
): string | null => {
  if (isNaN(value)) {
    return 'Must be a valid number';
  }

  if (value < min || value > max) {
    return `Must be between ${min} and ${max}`;
  }

  return null;
};

/**
 * Validate minimum length
 */
export const validateMinLength = (
  value: string,
  minLength: number,
  fieldName: string
): string | null => {
  if (!value) return null; // Skip if empty (use validateRequired for required fields)

  if (value.trim().length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`;
  }

  return null;
};

/**
 * Validate maximum length
 */
export const validateMaxLength = (
  value: string,
  maxLength: number,
  fieldName: string
): string | null => {
  if (!value) return null; // Skip if empty

  if (value.length > maxLength) {
    return `${fieldName} must be at most ${maxLength} characters`;
  }

  return null;
};

/**
 * Validate alphanumeric with dashes and underscores
 * Used for project codes, etc.
 */
export const validateAlphanumericWithDashes = (
  value: string,
  fieldName: string
): string | null => {
  if (!value) return null; // Allow empty for optional fields

  const alphanumericRegex = /^[a-zA-Z0-9_-]+$/;
  if (!alphanumericRegex.test(value)) {
    return `${fieldName} can only contain letters, numbers, dashes, and underscores`;
  }

  return null;
};

// ============================================================================
// FORM-LEVEL VALIDATORS
// ============================================================================

/**
 * Validate Employee form
 */
export const validateEmployeeForm = (formData: Partial<Employee>): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Required fields
  const firstNameError = validateRequired(formData.first_name, 'First name');
  if (firstNameError) errors.first_name = firstNameError;

  const lastNameError = validateRequired(formData.last_name, 'Last name');
  if (lastNameError) errors.last_name = lastNameError;

  const personalEmailError = validateRequired(formData.personal_email, 'Personal email');
  if (personalEmailError) errors.personal_email = personalEmailError;

  const locationTypeError = validateRequired(formData.location_type, 'Location type');
  if (locationTypeError) errors.location_type = locationTypeError;

  const employeeTypeError = validateRequired(formData.employee_type, 'Employee type');
  if (employeeTypeError) errors.employee_type = employeeTypeError;

  const employmentStatusError = validateRequired(formData.employment_status, 'Employment status');
  if (employmentStatusError) errors.employment_status = employmentStatusError;

  const hireDateError = validateRequired(formData.hire_date, 'Hire date');
  if (hireDateError) errors.hire_date = hireDateError;

  // Length validations
  if (formData.first_name) {
    const minLengthError = validateMinLength(formData.first_name, 1, 'First name');
    if (minLengthError) errors.first_name = minLengthError;

    const maxLengthError = validateMaxLength(formData.first_name, 100, 'First name');
    if (maxLengthError) errors.first_name = maxLengthError;
  }

  if (formData.middle_name) {
    const maxLengthError = validateMaxLength(formData.middle_name, 100, 'Middle name');
    if (maxLengthError) errors.middle_name = maxLengthError;
  }

  if (formData.last_name) {
    const minLengthError = validateMinLength(formData.last_name, 1, 'Last name');
    if (minLengthError) errors.last_name = minLengthError;

    const maxLengthError = validateMaxLength(formData.last_name, 100, 'Last name');
    if (maxLengthError) errors.last_name = maxLengthError;
  }

  // Email format validations
  if (formData.personal_email) {
    const emailError = validateEmail(formData.personal_email);
    if (emailError) errors.personal_email = emailError;
  }

  if (formData.work_email) {
    const workEmailError = validateEmail(formData.work_email);
    if (workEmailError) errors.work_email = workEmailError;
  }

  // Phone validation
  if (formData.phone) {
    const phoneError = validatePhone(formData.phone);
    if (phoneError) errors.phone = phoneError;
  }

  // Date range validation
  if (formData.hire_date && formData.end_date) {
    const dateRangeError = validateDateRange(
      formData.hire_date.toString(),
      formData.end_date.toString(),
      { start: 'Hire date', end: 'End date' }
    );
    if (dateRangeError) errors.end_date = dateRangeError;
  }

  // Address field length validations
  if (formData.address_line_1) {
    const maxError = validateMaxLength(formData.address_line_1, 200, 'Address line 1');
    if (maxError) errors.address_line_1 = maxError;
  }

  if (formData.address_line_2) {
    const maxError = validateMaxLength(formData.address_line_2, 200, 'Address line 2');
    if (maxError) errors.address_line_2 = maxError;
  }

  if (formData.city) {
    const maxError = validateMaxLength(formData.city, 100, 'City');
    if (maxError) errors.city = maxError;
  }

  if (formData.state) {
    const maxError = validateMaxLength(formData.state, 100, 'State');
    if (maxError) errors.state = maxError;
  }

  if (formData.zip_code) {
    const maxError = validateMaxLength(formData.zip_code, 20, 'Zip code');
    if (maxError) errors.zip_code = maxError;
  }

  return errors;
};

/**
 * Validate Project form
 */
export const validateProjectForm = (formData: Partial<Project>): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Required fields
  const nameError = validateRequired(formData.project_name, 'Project name');
  if (nameError) errors.project_name = nameError;

  const statusError = validateRequired(formData.status, 'Status');
  if (statusError) errors.status = statusError;

  const billingTypeError = validateRequired(formData.billing_type, 'Billing type');
  if (billingTypeError) errors.billing_type = billingTypeError;

  const clientNameError = validateRequired(formData.client_name, 'Client name');
  if (clientNameError) errors.client_name = clientNameError;

  // Length validations
  if (formData.project_name) {
    const minError = validateMinLength(formData.project_name, 2, 'Project name');
    if (minError) errors.project_name = minError;

    const maxError = validateMaxLength(formData.project_name, 200, 'Project name');
    if (maxError) errors.project_name = maxError;
  }

  if (formData.project_code) {
    const maxError = validateMaxLength(formData.project_code, 50, 'Project code');
    if (maxError) errors.project_code = maxError;

    const formatError = validateAlphanumericWithDashes(formData.project_code, 'Project code');
    if (formatError) errors.project_code = formatError;
  }

  if (formData.client_name) {
    const minError = validateMinLength(formData.client_name, 2, 'Client name');
    if (minError) errors.client_name = minError;

    const maxError = validateMaxLength(formData.client_name, 200, 'Client name');
    if (maxError) errors.client_name = maxError;
  }

  // Email validation
  if (formData.client_contact_email) {
    const emailError = validateEmail(formData.client_contact_email);
    if (emailError) errors.client_contact_email = emailError;
  }

  // Phone validation
  if (formData.client_contact_phone) {
    const phoneError = validatePhone(formData.client_contact_phone);
    if (phoneError) errors.client_contact_phone = phoneError;
  }

  // Date range validation
  if (formData.start_date && formData.end_date) {
    const dateRangeError = validateDateRange(
      formData.start_date.toString(),
      formData.end_date.toString(),
      { start: 'Start date', end: 'End date' }
    );
    if (dateRangeError) errors.end_date = dateRangeError;
  }

  if (formData.client_industry) {
    const maxError = validateMaxLength(formData.client_industry, 100, 'Client industry');
    if (maxError) errors.client_industry = maxError;
  }

  if (formData.client_address) {
    const maxError = validateMaxLength(formData.client_address, 500, 'Client address');
    if (maxError) errors.client_address = maxError;
  }

  return errors;
};

/**
 * Validate Allocation form
 */
export const validateAllocationForm = (
  formData: any,
  availableCapacity: number | null
): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Employee selection
  if (!formData.employee_id || formData.employee_id === 0) {
    errors.employee_id = 'Please select an employee';
  }

  // Allocation percentage - must be positive, no upper limit
  if (isNaN(formData.allocation_percentage) || formData.allocation_percentage < 1) {
    errors.allocation_percentage = 'Allocation percentage must be at least 1';
  }

  // Date range validation
  if (formData.start_date && formData.end_date) {
    const dateRangeError = validateDateRange(
      formData.start_date,
      formData.end_date,
      { start: 'Start date', end: 'End date' }
    );
    if (dateRangeError) errors.end_date = dateRangeError;
  }

  return errors;
};

/**
 * Validate Offboarding form
 */
export const validateOffboardingForm = (
  formData: any,
  isHROrAdmin: boolean
): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Required fields for HR/Admin
  if (isHROrAdmin) {
    const exitReasonError = validateRequired(formData.exit_reason, 'Exit reason');
    if (exitReasonError) errors.exit_reason = exitReasonError;

    const lastDayError = validateRequired(formData.last_working_day, 'Last working day');
    if (lastDayError) errors.last_working_day = lastDayError;
  }

  // Date range validation
  if (formData.resignation_date && formData.last_working_day) {
    const dateRangeError = validateDateRange(
      formData.resignation_date,
      formData.last_working_day,
      { start: 'Resignation date', end: 'Last working day' }
    );
    if (dateRangeError) errors.resignation_date = dateRangeError;
  }

  // Length validations
  if (formData.handover_completed_to) {
    const maxError = validateMaxLength(
      formData.handover_completed_to,
      200,
      'Handover completed to'
    );
    if (maxError) errors.handover_completed_to = maxError;
  }

  if (formData.exit_interview_notes) {
    const maxError = validateMaxLength(
      formData.exit_interview_notes,
      2000,
      'Exit interview notes'
    );
    if (maxError) errors.exit_interview_notes = maxError;
  }

  if (formData.notes) {
    const maxError = validateMaxLength(formData.notes, 2000, 'Notes');
    if (maxError) errors.notes = maxError;
  }

  // Satisfaction rating
  if (
    formData.satisfaction_rating !== null &&
    formData.satisfaction_rating !== undefined
  ) {
    const ratingError = validatePercentage(formData.satisfaction_rating, 0, 5);
    if (ratingError) errors.satisfaction_rating = ratingError;
  }

  return errors;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if errors object has any errors
 */
export const hasErrors = (errors: Record<string, string>): boolean => {
  return Object.keys(errors).length > 0;
};
